/**
 * Auth Context and Provider
 *
 * Provides authentication context to the entire app.
 * Handles auth state initialization and session validation.
 *
 * SECURITY: Zero client-side storage. Authentication is managed via a
 * single opaque session_id HTTP-only cookie. On every page load, we call
 * /auth/session to check if the session is valid and get user data. The
 * persistent cookie (7-day maxAge) keeps users logged in across browser sessions.
 *
 * @module AuthContext
 */

import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
// NOTE: No periodic token refresh timer. Session rotation is event-driven
// (login, privilege change, suspicious signals). The 7-day session_id cookie
// keeps the session alive; the backend refreshes Supabase tokens server-side
// on demand when the cached access token expires.
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../state_management_and_stores/auth.store';
import { authService, AuthError, friendlyAuthErrorMessage } from '../services/auth.service';
import { resetClientState } from '../lib/resetClientState';
import { useToast } from './ToastContext';

/**
 * Detect a soft-deactivated account in the auth response. Backend surfaces
 * the flag two ways:
 *   - `user.isActive` (top-level convenience) on /auth/session
 *   - `profile.is_active` (raw user_profiles column) everywhere
 * We accept either; both default to "active" so a missing field is treated
 * as fully active.
 */
function isAccountDeactivated(response: {
  user?: { isActive?: boolean } | unknown;
  profile?: { is_active?: boolean } | null;
}): boolean {
  const u: any = response.user;
  const p: any = response.profile;
  if (u && u.isActive === false) return true;
  if (p && p.is_active === false) return true;
  return false;
}

/**
 * Pull the optional admin-supplied deactivation reason out of an auth
 * response. /auth/session puts it on `user.deactivationReason`; every
 * other endpoint returns the raw column on `profile.deactivation_reason`.
 * Returns null when no reason was attached (or when the account is
 * actually active, which shouldn't happen at the callsite but is harmless).
 */
function getDeactivationReason(response: {
  user?: { deactivationReason?: string | null } | unknown;
  profile?: { deactivation_reason?: string | null } | null;
}): string | null {
  const u: any = response.user;
  const p: any = response.profile;
  return u?.deactivationReason ?? p?.deactivation_reason ?? null;
}

/**
 * Auth context type
 */
interface AuthContextType {
  // Auth state from store
  isAuthenticated: boolean;
  isLoading: boolean;

  // Auth actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    acceptTerms: boolean
  ) => Promise<{ needsEmailConfirmation: boolean; alreadyExisted: boolean }>;

  // Google OAuth
  loginWithGoogle: () => Promise<void>;
  handleOAuthCallback: (code: string) => Promise<void>;
  handleOAuthTokens: (accessToken: string, refreshToken: string) => Promise<void>;

  // Token refresh
  refreshAccessToken: () => Promise<void>;
}

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth Provider Props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth Provider Component
 *
 * Wraps the app and provides auth context.
 * On mount, validates session via backend cookie check.
 * Token refresh is on-demand only (no periodic timer).
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    setAuthData,
    logout: logoutStore,
    setLoading,
  } = useAuthStore();

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const initRef = useRef(false);

  /**
   * Send a soft-deactivated user to the landing page after clearing every
   * trace of their session. Backend already refuses to mint a new session
   * for them, but until they sign out the cookie is still authoritative for
   * read endpoints — calling /auth/logout clears it explicitly. We use a
   * hard navigation (window.location) because AuthContext sits above the
   * router and doesn't have a useNavigate handle, and because we want a
   * fresh React tree mounted (no leaking query cache / store state into the
   * landing page).
   */
  const sendToDeactivatedLanding = useCallback(
    async (email?: string | null, reason?: string | null) => {
      try {
        await authService.logout();
      } catch {
        // Best-effort — even if the call fails we still want to clear local state.
      }
      logoutStore();
      resetClientState(queryClient);
      const params = new URLSearchParams();
      if (email) params.set('email', email);
      if (reason) params.set('reason', reason);
      const qs = params.toString();
      window.location.assign(`/account-deactivated${qs ? `?${qs}` : ''}`);
    },
    [logoutStore, queryClient],
  );

  /**
   * Refresh session via backend (on-demand only)
   *
   * The backend reads the session_id from the HTTP-only cookie and
   * refreshes the Supabase token server-side if it has expired.
   * No session rotation — the session_id cookie stays the same.
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await authService.refreshSession();

      if (isAccountDeactivated(response)) {
        await sendToDeactivatedLanding(
          response.user?.email,
          getDeactivationReason(response),
        );
        return;
      }

      // Update in-memory state with refreshed user data
      setAuthData({
        user: response.user,
        profile: response.profile,
        role: response.role,
        permissions: response.permissions,
        impersonator: response.impersonator ?? null,
        termsAcceptanceRequired: response.termsAcceptanceRequired ?? false,
      });

      console.log('[Auth] Token refreshed via backend');
    } catch (error) {
      console.error('[Auth] Token refresh failed:', error);
      // If refresh fails, session is gone — log out
      logoutStore();
    }
  }, [setAuthData, logoutStore, sendToDeactivatedLanding]);

  /**
   * Initialize auth state on mount
   *
   * Call /auth/session to check if HTTP-only cookies are valid.
   * If valid, populate in-memory Zustand store with user data.
   * If not, user sees the login screen.
   */
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initAuth = async () => {
      setLoading(true);
      try {
        console.log('[Auth] Checking session via cookies...');
        const sessionData = await authService.validateSession();

        // Deactivated accounts may still authenticate (so we *can* read
        // their session), but as soon as we see is_active=false we kick
        // them to the landing page. This catches the "user already
        // logged in when admin clicked Deactivate" race.
        if (isAccountDeactivated(sessionData)) {
          await sendToDeactivatedLanding(
            sessionData.user?.email,
            getDeactivationReason(sessionData),
          );
          return;
        }

        // Cookies valid — populate in-memory store
        setAuthData({
          user: sessionData.user,
          profile: sessionData.profile,
          role: sessionData.role,
          permissions: sessionData.permissions,
          impersonator: sessionData.impersonator ?? null,
          termsAcceptanceRequired: sessionData.termsAcceptanceRequired ?? false,
        });
        console.log('[Auth] Session restored from cookies');
      } catch {
        // No valid cookies — user is not logged in
        console.log('[Auth] No valid session cookies');
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login handler
   */
  const handleLogin = async (email: string, password: string) => {
    try {
      setLoading(true);

      const response = await authService.login(email, password);

      // Per the spec: deactivated users may authenticate but must be
      // immediately logged out and routed to the explanation page. Doing
      // this before resetClientState/setAuthData prevents any momentary
      // dashboard flash.
      if (isAccountDeactivated(response)) {
        await sendToDeactivatedLanding(
          response.user?.email || email,
          getDeactivationReason(response),
        );
        return;
      }

      // Defense in depth: clear any data the previous user left behind
      // before populating the store with the new user's data. If logout
      // cleanup was skipped (session expiry, another tab, crash), this
      // guarantees the next user never sees stale registrations/etc.
      resetClientState(queryClient);

      // Store user data in memory (tokens are in cookies)
      setAuthData({
        user: response.user,
        profile: response.profile,
        role: response.role,
        permissions: response.permissions,
        impersonator: response.impersonator ?? null,
        termsAcceptanceRequired: response.termsAcceptanceRequired ?? false,
      });

      showSuccess('Welcome Back!', 'Login successful');
      console.log('[Auth] Login successful');
    } catch (error) {
      console.error('[Auth] Login error:', error);
      const authError = error as AuthError;
      showError('Login Failed', authError.message || 'Invalid email or password');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register handler
   */
  const handleRegister = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    acceptTerms: boolean
  ): Promise<{ needsEmailConfirmation: boolean; alreadyExisted: boolean }> => {
    try {
      setLoading(true);

      const signupResponse = await authService.register(email, password, firstName, lastName, acceptTerms);
      const alreadyExisted = signupResponse.alreadyExisted === true;

      // When email confirmation is required (Supabase mailer_autoconfirm=false),
      // the backend creates the user but there's no session cookie yet — the
      // user has to click the confirmation email link before they can log in.
      // Don't try to fetch /me; SignupPage will navigate to the dedicated
      // RegistrationSuccessPage which surfaces the "check your email" message.
      if (signupResponse.needsEmailConfirmation) {
        console.log(
          alreadyExisted
            ? '[Auth] Re-registration suppressed — original confirmation email still pending'
            : '[Auth] Registration successful — confirmation email pending',
        );
        return { needsEmailConfirmation: true, alreadyExisted };
      }

      // Auto-confirmed path: user has a session cookie, fetch their full profile
      const userData = await authService.getCurrentUser();

      // Defense in depth — clear any stale data left in cache/stores.
      resetClientState(queryClient);

      // Store in memory (tokens are in cookies)
      setAuthData({
        user: { id: userData.user.id, email: userData.user.email },
        profile: userData.profile,
        role: userData.role,
        permissions: userData.permissions,
        impersonator: userData.impersonator ?? null,
        termsAcceptanceRequired: userData.termsAcceptanceRequired ?? false,
      });

      showSuccess('Account Created!', 'Welcome to the platform');
      console.log('[Auth] Registration successful');
      return { needsEmailConfirmation: false, alreadyExisted: false };
    } catch (error) {
      console.error('[Auth] Registration error:', error);
      showError('Registration Failed', friendlyAuthErrorMessage(error, 'Unable to create account. Please try again.'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Google OAuth login — redirect to Google via backend-generated URL
   */
  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      const url = await authService.getGoogleOAuthUrl();
      // Redirect to Google OAuth consent page
      window.location.href = url;
    } catch (error) {
      console.error('[Auth] Google OAuth error:', error);
      const authError = error as AuthError;
      showError('Google Login Failed', authError.message || 'Unable to initiate Google login');
      setLoading(false);
      throw error;
    }
  };

  /**
   * Handle OAuth callback — exchange code for session
   */
  const handleOAuthCallback = async (code: string) => {
    try {
      setLoading(true);

      const response = await authService.validateOAuth(code);

      if (isAccountDeactivated(response)) {
        await sendToDeactivatedLanding(
          response.user?.email,
          getDeactivationReason(response),
        );
        return;
      }

      // Same defense as password login — wipe prior client state before
      // hydrating the new identity.
      resetClientState(queryClient);

      setAuthData({
        user: response.user,
        profile: response.profile,
        role: response.role,
        permissions: response.permissions,
        impersonator: response.impersonator ?? null,
        termsAcceptanceRequired: response.termsAcceptanceRequired ?? false,
      });

      showSuccess('Welcome!', 'Google login successful');
      console.log('[Auth] OAuth callback successful');
    } catch (error) {
      console.error('[Auth] OAuth callback error:', error);
      const authError = error as AuthError;
      showError('Login Failed', authError.message || 'Unable to complete Google login');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle OAuth implicit flow — send tokens to backend for session creation
   */
  const handleOAuthTokens = async (accessToken: string, refreshToken: string) => {
    try {
      setLoading(true);

      const response = await authService.exchangeOAuthTokens(accessToken, refreshToken);

      if (isAccountDeactivated(response)) {
        await sendToDeactivatedLanding(
          response.user?.email,
          getDeactivationReason(response),
        );
        return;
      }

      // Same defense as password login — wipe prior client state before
      // hydrating the new identity.
      resetClientState(queryClient);

      setAuthData({
        user: response.user,
        profile: response.profile,
        role: response.role,
        permissions: response.permissions,
        impersonator: response.impersonator ?? null,
        termsAcceptanceRequired: response.termsAcceptanceRequired ?? false,
      });

      showSuccess('Welcome!', 'Google login successful');
      console.log('[Auth] OAuth token exchange successful');
    } catch (error) {
      console.error('[Auth] OAuth token exchange error:', error);
      const authError = error as AuthError;
      showError('Login Failed', authError.message || 'Unable to complete Google login');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout handler
   */
  const handleLogout = async () => {
    try {
      setLoading(true);

      // Call logout API — backend clears cookies
      await authService.logout();

      // Clear in-memory state
      logoutStore();

      // Wipe React Query cache + every Zustand store holding user-scoped data.
      // Without this, components mount on the next login and read whatever
      // data was last cached for the previous user (e.g. buddy-group
      // registrations visible to the primary still showing for a member).
      resetClientState(queryClient);

      showSuccess('Logged Out', 'You have been logged out successfully');
      console.log('[Auth] Logout successful');
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      // Still clear in-memory state even if API call fails
      logoutStore();
      resetClientState(queryClient);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Context value
   */
  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    loginWithGoogle: handleGoogleLogin,
    handleOAuthCallback,
    handleOAuthTokens,
    refreshAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth hook
 *
 * Access auth context from any component.
 * Throws error if used outside AuthProvider.
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
